// Website Creator: htutkhaung.ko@msgtech.co.th
import { getLanguage } from './i18n.js';
/**
 * Mala Restaurant Integrated Weight Scale POS Simulator & Dynamic Scrollytelling Engine
 */

export function initMalaScaleSim() {
  const section = document.getElementById('mala-pos');
  const simWrapper = document.getElementById('mala-scale-sim');
  if (!simWrapper) return;

  // DOM Elements - Weight Dialog & Scale Platter
  const scaleLcd = document.getElementById('scale-lcd-val');
  const netWeightDisplay = document.getElementById('sim-net-weight');
  const grossWeightDisplay = document.getElementById('sim-gross-weight');
  const tareWeightDisplay = document.getElementById('sim-tare-weight');
  const scaleLogTerminal = document.getElementById('scale-log-terminal');
  const scalePlatter = document.getElementById('scale-platter-plate');
  const bowlItemsContainer = document.getElementById('sim-bowl-items');
  const streamStatusBadge = document.getElementById('sim-stream-status');
  const tareToggleBtn = document.getElementById('btn-tare-toggle');
  const tareStatusBadge = document.getElementById('tare-status-badge');
  const weightSlider = document.getElementById('sim-weight-slider');

  // DOM Elements - System Reaction Banner & Dual POS
  const reactionBanner = document.getElementById('system-reaction-banner');
  const reactionIcon = document.getElementById('system-reaction-icon');
  const reactionTitle = document.getElementById('system-reaction-title');
  const reactionDesc = document.getElementById('system-reaction-desc');

  const customerWeightDisplay = document.getElementById('cust-weight');
  const customerRateDisplay = document.getElementById('cust-rate');
  const customerTotalDisplay = document.getElementById('cust-total');
  const customerSpiceDisplay = document.getElementById('cust-spice');
  const ticketWeightDisplay = document.getElementById('ticket-weight');
  const ticketTotalDisplay = document.getElementById('ticket-total');
  const thermalTicket = document.getElementById('sim-thermal-ticket');
  const qrContainer = document.getElementById('sim-qr-box');
  const qrStatusText = document.getElementById('sim-qr-status');

  // Scrollytelling controls & step elements
  const scrollyStepText = document.getElementById('scrolly-step-text');
  const scrollyDots = document.querySelectorAll('.scrolly-dot');
  const stepButtons = document.querySelectorAll('.scrolly-step-btn');
  const autoPlayBtn = document.getElementById('btn-auto-play-sim');

  const trayButtons = document.querySelectorAll('.sim-tray-btn');
  const brothButtons = document.querySelectorAll('.sim-broth-btn');

  // Synthesized audio feedback
  let lastAudioTime = 0;
  function playTickSound(frequency = 680, duration = 0.08) {
    const now = Date.now();
    if (now - lastAudioTime < 60) return;
    lastAudioTime = now;

    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(frequency, ctx.currentTime);
      gain.gain.setValueAtTime(0.04, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + duration);
    } catch (e) {}
  }

  // State
  let bowlTareActive = true;
  const BOWL_WEIGHT = 120.0; // 120g tare
  let currentGrossWeight = 120.0;
  let pricePer100g = 45.0; // ฿45.00 / 100g
  let selectedBroth = 'Signature Mala Stir-Fry (Medium)';
  let activeItems = [];
  let currentStageIndex = 0;
  let isAutoPlaying = false;
  let autoPlayTimer = null;
  let manualInteracted = false;

  // Number animation helper
  let displayedTotal = 0;
  let displayedWeight = 0;
  let animFrameId = null;

  function animateNumbers(targetWeight, targetTotal) {
    if (animFrameId) cancelAnimationFrame(animFrameId);

    const startWeight = displayedWeight;
    const startTotal = displayedTotal;
    const startTime = performance.now();
    const duration = 320; // 320ms smooth ease

    function step(now) {
      const elapsed = now - startTime;
      const progress = Math.min(1, elapsed / duration);
      const ease = 1 - Math.pow(1 - progress, 3); // ease-out cubic

      displayedWeight = startWeight + (targetWeight - startWeight) * ease;
      displayedTotal = startTotal + (targetTotal - startTotal) * ease;

      if (scaleLcd) scaleLcd.textContent = `${displayedWeight.toFixed(1)} g`;
      if (customerWeightDisplay) customerWeightDisplay.textContent = `${displayedWeight.toFixed(1)} g`;
      if (customerTotalDisplay) customerTotalDisplay.textContent = `฿${displayedTotal.toFixed(2)}`;
      if (ticketWeightDisplay) ticketWeightDisplay.textContent = `${displayedWeight.toFixed(1)}g NET`;
      if (ticketTotalDisplay) ticketTotalDisplay.textContent = `฿${displayedTotal.toFixed(2)}`;

      if (progress < 1) {
        animFrameId = requestAnimationFrame(step);
      } else {
        displayedWeight = targetWeight;
        displayedTotal = targetTotal;
      }
    }

    animFrameId = requestAnimationFrame(step);
  }

  // 5 Scrollytelling Stages (bilingual Thai/English)
  const SCROLL_STAGES = [
    {
      stage: 0,
      title: "Step 1/5: Empty Bowl Placed & Auto-Tare Calibrated (-120.0g)",
      title_th: "ขั้นตอน 1/5: วางชามเปล่าบนตาชั่ง & หักน้ำหนักชามอัตโนมัติ (-120.0g)",
      gross: 120.0,
      items: [],
      stabilized: false,
      log: "> RS232: [STX] 0000.0g NET [ETX] • AUTO-TARE -120g APPLIED • ZERO CALIBRATED",
      reactionTitle: "System Listening to Scale Stream...",
      reactionTitle_th: "ระบบกำลังรับสัญญาณจากเครื่องชั่งดิจิทัล...",
      reactionDesc: "Awaiting ingredients on platter. POS is ready for sub-millisecond digital weight streaming.",
      reactionDesc_th: "วางวัตถุดิบลงบนถาดชั่ง ระบบ POS พร้อมคำนวณราคาสดในระดับมิลลิวินาที",
      reactionStatus: "listening",
    },
    {
      stage: 1,
      title: "Step 2/5: Sliced Angus Beef Added (+140g)",
      title_th: "ขั้นตอน 2/5: วางเนื้อสไลซ์แองกัสลงชาม (+140g)",
      gross: 260.0,
      items: [
        { name: "Angus Beef", name_th: "เนื้อแองกัส", weight: 140.0, icon: "🥩" }
      ],
      stabilized: false,
      log: "> RS232: [STX] 0140.0g NET [ETX] • SENSOR STABLE • ACK RECEIVED BY POS",
      reactionTitle: "⚡ Item Detected: +140.0g Sliced Angus Beef!",
      reactionTitle_th: "⚡ ตรวจพบวัตถุดิบ: +140.0g เนื้อสไลซ์แองกัส!",
      reactionDesc: "POS Core recalculated in 8ms: 140.0g Net @ ฿45.00/100g = ฿63.00",
      reactionDesc_th: "ระบบ POS คำนวณใน 8ms: สุทธิ 140.0g @ ฿45.00/100g = ฿63.00",
      reactionStatus: "active",
    },
    {
      stage: 2,
      title: "Step 3/5: Enoki & Crispy Lotus Root Added (+110g)",
      title_th: "ขั้นตอน 3/5: เพิ่มเห็ดเข็มทองและรากบัวกรอบ (+110g)",
      gross: 370.0,
      items: [
        { name: "Angus Beef", name_th: "เนื้อแองกัส", weight: 140.0, icon: "🥩" },
        { name: "Enoki & Lotus", name_th: "เห็ดเข็มทอง & รากบัว", weight: 110.0, icon: "🥬" }
      ],
      stabilized: false,
      log: "> RS232: [STX] 0250.0g NET [ETX] • ACCUMULATED LOAD VERIFIED",
      reactionTitle: "⚡ Item Detected: +110.0g Lotus Root & Enoki!",
      reactionTitle_th: "⚡ ตรวจพบวัตถุดิบ: +110.0g เห็ดเข็มทอง & รากบัว!",
      reactionDesc: "POS Core recalculated: 250.0g Net Weight = ฿112.50 total",
      reactionDesc_th: "ระบบ POS อัปเดตราคาสด: น้ำหนักสุทธิ 250.0g = รวม ฿112.50",
      reactionStatus: "active",
    },
    {
      stage: 3,
      title: "Step 4/5: Fish Tofu & Seafood Meatballs Added (+125g)",
      title_th: "ขั้นตอน 4/5: เพิ่มเต้าหู้ปลาและลูกชิ้นซีฟู้ด (+125g)",
      gross: 495.0,
      items: [
        { name: "Angus Beef", name_th: "เนื้อแองกัส", weight: 140.0, icon: "🥩" },
        { name: "Enoki & Lotus", name_th: "เห็ดเข็มทอง & รากบัว", weight: 110.0, icon: "🥬" },
        { name: "Meatballs & Tofu", name_th: "ลูกชิ้น & เต้าหู้ปลา", weight: 125.0, icon: "🍢" }
      ],
      stabilized: false,
      log: "> RS232: [STX] 0375.0g NET [ETX] • FINAL INGREDIENT IN BOWL DETECTED",
      reactionTitle: "⚡ Item Detected: +125.0g Fish Tofu & Meatballs!",
      reactionTitle_th: "⚡ ตรวจพบวัตถุดิบ: +125.0g เต้าหู้ปลา & ลูกชิ้น!",
      reactionDesc: "POS Core recalculated: 375.0g Net Weight = ฿168.75 total",
      reactionDesc_th: "ระบบ POS คำนวณยอดสะสม: น้ำหนักสุทธิ 375.0g = รวม ฿168.75",
      reactionStatus: "active",
    },
    {
      stage: 4,
      title: "Step 5/5: Weight Stabilized • PromptPay QR Active & Ticket Printed",
      title_th: "ขั้นตอน 5/5: น้ำหนักนิ่งแล้ว • สร้าง PromptPay QR & พิมพ์สลิปส่งครัว",
      gross: 495.0,
      items: [
        { name: "Angus Beef", name_th: "เนื้อแองกัส", weight: 140.0, icon: "🥩" },
        { name: "Enoki & Lotus", name_th: "เห็ดเข็มทอง & รากบัว", weight: 110.0, icon: "🥬" },
        { name: "Meatballs & Tofu", name_th: "ลูกชิ้น & เต้าหู้ปลา", weight: 125.0, icon: "🍢" }
      ],
      stabilized: true,
      log: "> RS232: [STX] 0375.0g [ETX] • STABILIZED (0ms JITTER) • GENERATE_PROMPTPAY_QR • PRINT_KDS_SLIP",
      reactionTitle: "✅ Weight Stabilized & Locked at 375.0g Net!",
      reactionTitle_th: "✅ น้ำหนักนิ่งแล้วที่ 375.0g สุทธิ พร้อมชำระเงิน!",
      reactionDesc: "Zero-margin human error confirmed. Dynamic PromptPay QR active for ฿168.75 & KDS barcode printed.",
      reactionDesc_th: "ตัดข้อผิดพลาดมนุษย์ 100% สแกนจ่าย PromptPay ฿168.75 ทันที พร้อมพิมพ์บาร์โค้ดส่งครัว KDS",
      reactionStatus: "stabilized",
    }
  ];

  function updateUi(stageData, triggerBounce = true, isScroll = false) {
    const tare = bowlTareActive ? BOWL_WEIGHT : 0.0;
    const netWeight = Math.max(0, stageData.gross - tare);
    const totalPrice = (netWeight / 100.0) * pricePer100g;

    // 1. Platter bounce (only on interactive click or auto-play, never during scroll)
    if (triggerBounce && !isScroll && scalePlatter && stageData.items.length > 0) {
      scalePlatter.classList.remove('bounce-active');
      scalePlatter.classList.add('bounce-active');
      setTimeout(() => scalePlatter.classList.remove('bounce-active'), 400);
    }

    // 2. Animate numbers: instant lockstep when scrolling for 0ms lag; smooth ease on click
    if (isScroll) {
      if (animFrameId) cancelAnimationFrame(animFrameId);
      displayedWeight = netWeight;
      displayedTotal = totalPrice;
      if (scaleLcd) scaleLcd.textContent = `${netWeight.toFixed(1)} g`;
      if (customerWeightDisplay) customerWeightDisplay.textContent = `${netWeight.toFixed(1)} g`;
      if (customerTotalDisplay) customerTotalDisplay.textContent = `฿${totalPrice.toFixed(2)}`;
      if (ticketWeightDisplay) ticketWeightDisplay.textContent = `${netWeight.toFixed(1)}g NET`;
      if (ticketTotalDisplay) ticketTotalDisplay.textContent = `฿${totalPrice.toFixed(2)}`;
    } else {
      animateNumbers(netWeight, totalPrice);
    }

    // 3. Telemetry displays
    const isThai = getLanguage() === 'th';

    if (grossWeightDisplay) grossWeightDisplay.textContent = `${stageData.gross.toFixed(1)}g`;
    if (tareWeightDisplay) tareWeightDisplay.textContent = `${tare > 0 ? '-' : ''}${tare.toFixed(1)}g`;
    if (netWeightDisplay) netWeightDisplay.textContent = `${netWeight.toFixed(1)}g`;
    if (customerRateDisplay) customerRateDisplay.textContent = isThai ? `฿${pricePer100g.toFixed(2)} / 100 กรัม` : `฿${pricePer100g.toFixed(2)} / 100g`;
    if (customerSpiceDisplay) customerSpiceDisplay.textContent = selectedBroth;
    if (weightSlider) weightSlider.value = stageData.gross;

    // 4. Update Scale RS-232 Log Terminal
    if (scaleLogTerminal) {
      scaleLogTerminal.textContent = stageData.log;
    }

    // 5. Update Platter Bowl Visual with fixed 3-item height from the start
    if (bowlItemsContainer) {
      if (stageData.items.length === 0) {
        bowlItemsContainer.innerHTML = `
          <div class="bowl-empty-state">
            <span style="font-weight: 700; color: #475569; font-size: 0.8rem;">🥣 ${isThai ? 'ชามพร้อมวางวัตถุดิบ' : 'Empty Bowl Ready'}</span>
            <span style="font-size: 0.72rem; color: #94a3b8;">${isThai ? 'หักน้ำหนักชามแล้ว (-120.0g) • รอเพิ่มวัตถุดิบ' : 'Auto-Tare Calibrated (-120.0g) • Awaiting items'}</span>
          </div>
        `;
      } else {
        bowlItemsContainer.innerHTML = stageData.items.map((item, idx) => {
          const itemName = isThai && item.name_th ? item.name_th : item.name;
          return `
            <div class="bowl-ingredient-item" style="animation-delay: ${idx * 0.05}s;">
              <div style="display: flex; align-items: center; gap: 7px;">
                <span style="font-size: 0.95rem;">${item.icon}</span>
                <span style="font-weight: 700;">${itemName}</span>
              </div>
              <span style="color: #2563eb; font-weight: 800; font-family: monospace; font-size: 0.76rem;">+${item.weight}g</span>
            </div>
          `;
        }).join('');
      }
    }

    // 6. Update System Reaction Notification Banner
    if (reactionBanner && reactionTitle && reactionDesc && reactionIcon) {
      reactionBanner.className = 'system-reaction-banner';
      if (stageData.reactionStatus === 'stabilized') {
        reactionBanner.classList.add('stabilized-notice');
        reactionIcon.textContent = '✅';
      } else if (stageData.reactionStatus === 'active') {
        reactionBanner.classList.add('active-notice');
        reactionIcon.textContent = '⚡';
      } else {
        reactionIcon.textContent = '🥣';
      }

      reactionTitle.textContent = isThai && stageData.reactionTitle_th ? stageData.reactionTitle_th : stageData.reactionTitle;
      reactionDesc.textContent = isThai && stageData.reactionDesc_th ? stageData.reactionDesc_th : stageData.reactionDesc;
    }

    // 7. Stream status & PromptPay QR
    if (streamStatusBadge) {
      if (stageData.stabilized) {
        streamStatusBadge.textContent = isThai ? "● น้ำหนักนิ่งแล้ว (LOCKED)" : "● STABILIZED (LOCKED)";
        streamStatusBadge.style.color = "#10b981";
      } else {
        streamStatusBadge.textContent = isThai ? "● กำลังสตรีมข้อมูล (10ms)" : "● STREAMING (10ms)";
        streamStatusBadge.style.color = "#38bdf8";
      }
    }

    if (qrContainer && qrStatusText) {
      if (stageData.stabilized && netWeight > 0) {
        qrContainer.style.opacity = "1";
        qrContainer.style.filter = "none";
        qrContainer.style.borderColor = "#10b981";
        qrContainer.style.background = "rgba(16, 185, 129, 0.08)";
        qrStatusText.innerHTML = isThai 
          ? `<strong style="color: #059669;">✓ พร้อมสแกน Thai PromptPay QR</strong><br/>สแกนจ่ายผ่านแอปพลิเคชันธนาคารไทยได้ทุกธนาคาร ยอดชำระ <strong>฿${totalPrice.toFixed(2)}</strong>.`
          : `<strong style="color: #059669;">✓ PromptPay QR Active</strong><br/>Scan with any Thai mobile banking app to pay <strong>฿${totalPrice.toFixed(2)}</strong>.`;
      } else {
        qrContainer.style.opacity = "0.5";
        qrContainer.style.filter = "grayscale(70%)";
        qrContainer.style.borderColor = "rgba(37, 99, 235, 0.15)";
        qrContainer.style.background = "rgba(37, 99, 235, 0.05)";
        qrStatusText.innerHTML = isThai
          ? `<strong style="color: #64748b;">กำลังรอน้ำหนักนิ่ง...</strong><br/>ใส่วัตถุดิบเพื่อสร้าง PromptPay QR อัตโนมัติ`
          : `<strong style="color: #64748b;">Awaiting Weight Stabilization...</strong><br/>Place ingredients to generate dynamic QR.`;
      }
    }

    // 8. Thermal ticket animation
    if (thermalTicket && stageData.stabilized && !isScroll) {
      thermalTicket.style.animation = 'ticketPrintSlide 0.5s ease-out forwards';
    }

    // 9. Step Headers & Buttons
    if (scrollyStepText) {
      scrollyStepText.textContent = isThai && stageData.title_th ? stageData.title_th : stageData.title;
    }

    scrollyDots.forEach((dot, idx) => {
      dot.classList.toggle('active', idx === stageData.stage);
      dot.style.background = idx === stageData.stage ? '#2563eb' : '#cbd5e1';
    });

    stepButtons.forEach((btn, idx) => {
      btn.classList.toggle('active', idx === stageData.stage);
    });
  }

  function applyStage(stageIdx, triggerBounce = true, isInteractive = false, isScroll = false) {
    if (stageIdx < 0 || stageIdx >= SCROLL_STAGES.length) return;
    currentStageIndex = stageIdx;
    const data = SCROLL_STAGES[stageIdx];
    currentGrossWeight = data.gross;
    activeItems = [...data.items];

    updateUi(data, triggerBounce, isScroll);
    if (isInteractive) {
      playTickSound(520 + stageIdx * 110);
    }
  }

  // ─── Scroll-Driven Scrollytelling Engine with Hysteresis ───
  let scrollLockUntil = 0;
  let scrollTicking = false;

  function calculateScrollStage(progress, current) {
    // 3.5% hysteresis deadband prevents boundary oscillation jitter
    const band = 0.035;
    if (current === 0) {
      if (progress > 0.18 + band) return progress > 0.40 ? (progress > 0.62 ? (progress > 0.84 ? 4 : 3) : 2) : 1;
      return 0;
    }
    if (current === 1) {
      if (progress < 0.18 - band) return 0;
      if (progress > 0.40 + band) return progress > 0.62 ? (progress > 0.84 ? 4 : 3) : 2;
      return 1;
    }
    if (current === 2) {
      if (progress < 0.40 - band) return progress < 0.18 ? 0 : 1;
      if (progress > 0.62 + band) return progress > 0.84 ? 4 : 3;
      return 2;
    }
    if (current === 3) {
      if (progress < 0.62 - band) return progress < 0.40 ? (progress < 0.18 ? 0 : 1) : 2;
      if (progress > 0.84 + band) return 4;
      return 3;
    }
    if (current === 4) {
      if (progress < 0.84 - band) return progress < 0.62 ? (progress < 0.40 ? (progress < 0.18 ? 0 : 1) : 2) : 3;
      return 4;
    }
    return 0;
  }

  // Only track scroll when the Mala simulator section is actually near/in viewport
  let isSimNearViewport = false;
  if ('IntersectionObserver' in window && simWrapper) {
    const simViewportObserver = new IntersectionObserver((entries) => {
      isSimNearViewport = entries[0].isIntersecting;
    }, { rootMargin: '250px 0px' });
    simViewportObserver.observe(simWrapper);
  } else {
    isSimNearViewport = true;
  }

  function handleScroll() {
    if (!isSimNearViewport) return;
    if (isAutoPlaying || manualInteracted) return;
    if (Date.now() < scrollLockUntil) return;
    if (!section || !simWrapper) return;

    const rect = simWrapper.getBoundingClientRect();
    const windowH = window.innerHeight;

    // When the simulator workbench enters viewport scrubbing range
    if (rect.top <= windowH * 0.72 && rect.bottom >= windowH * 0.15) {
      const scrollableSpan = rect.height + windowH * 0.45;
      const progress = Math.max(0, Math.min(1, (windowH * 0.72 - rect.top) / scrollableSpan));

      const targetStage = calculateScrollStage(progress, currentStageIndex);

      if (targetStage !== currentStageIndex) {
        applyStage(targetStage, false, false, true);
      }
    }
  }

  window.addEventListener('scroll', () => {
    if (!isSimNearViewport) return;
    if (!scrollTicking) {
      requestAnimationFrame(() => {
        handleScroll();
        scrollTicking = false;
      });
      scrollTicking = true;
    }
  }, { passive: true });

  // ─── Direct Step Buttons & Dots ───
  stepButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      scrollLockUntil = Date.now() + 2000;
      if (isAutoPlaying) stopAutoPlay();
      const targetStep = parseInt(btn.getAttribute('data-step') || '0', 10);
      applyStage(targetStep, true, true, false);
    });
  });

  scrollyDots.forEach(dot => {
    dot.addEventListener('click', () => {
      scrollLockUntil = Date.now() + 2000;
      if (isAutoPlaying) stopAutoPlay();
      const targetStep = parseInt(dot.getAttribute('data-step-target') || '0', 10);
      applyStage(targetStep, true, true, false);
    });
  });

  // ─── Auto-Play Demo Engine ───
  function stopAutoPlay() {
    isAutoPlaying = false;
    if (autoPlayTimer) clearInterval(autoPlayTimer);
    if (autoPlayBtn) autoPlayBtn.innerHTML = '<span>▶ Auto-Play Demo</span>';
  }

  if (autoPlayBtn) {
    autoPlayBtn.addEventListener('click', () => {
      if (isAutoPlaying) {
        stopAutoPlay();
        return;
      }

      isAutoPlaying = true;
      manualInteracted = false;
      autoPlayBtn.innerHTML = '<span>⏸ Pause Demo</span>';

      let step = 0;
      applyStage(0, true, true, false);

      autoPlayTimer = setInterval(() => {
        step = (step + 1) % SCROLL_STAGES.length;
        applyStage(step, true, true, false);
        if (step === SCROLL_STAGES.length - 1) {
          // Pause slightly on the final stabilized ticket stage
          setTimeout(() => {
            if (isAutoPlaying) applyStage(0, true, true, false);
          }, 3500);
        }
      }, 1800);
    });
  }

  // ─── Interactive Manual Controls (Slider, Tray, Tare, Broths) ───
  if (weightSlider) {
    weightSlider.addEventListener('input', (e) => {
      manualInteracted = true;
      if (isAutoPlaying) stopAutoPlay();
      currentGrossWeight = parseFloat(e.target.value);

      const customStage = {
        stage: -1,
        title: `Manual Scale Test: ${currentGrossWeight.toFixed(1)}g Load Placed`,
        gross: currentGrossWeight,
        items: activeItems,
        stabilized: currentGrossWeight > 350,
        log: `> RS232: MANUAL SLIDER LOAD: ${currentGrossWeight.toFixed(1)}g`,
        reactionTitle: `⚡ Scale Event: ${currentGrossWeight.toFixed(1)}g Placed On Scale`,
        reactionDesc: `System detected load. Calculating order total in real-time.`,
        reactionStatus: currentGrossWeight > 350 ? 'stabilized' : 'active',
      };
      updateUi(customStage, false, false);
      playTickSound(450 + currentGrossWeight * 0.4);
    });
  }

  trayButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      manualInteracted = true;
      if (isAutoPlaying) stopAutoPlay();

      const addGrams = parseFloat(btn.getAttribute('data-add-weight') || '0');
      const rawName = btn.getAttribute('data-item-name') || 'Ingredient';
      const cleanName = rawName.replace(/^[\p{Emoji}\s]+/u, '').trim();
      currentGrossWeight = Math.min(950, currentGrossWeight + addGrams);

      let icon = "🥢";
      if (rawName.includes('Beef')) icon = "🥩";
      else if (rawName.includes('Enoki') || rawName.includes('Lotus')) icon = "🥬";
      else if (rawName.includes('Fish') || rawName.includes('Meatball') || rawName.includes('Tofu')) icon = "🍢";
      else if (rawName.includes('Mushroom') || rawName.includes('Shiitake')) icon = "🍄";

      activeItems.push({ name: cleanName, weight: addGrams, icon });

      const customStage = {
        stage: -1,
        title: `Item Added: ${cleanName} (+${addGrams}g)`,
        gross: currentGrossWeight,
        items: activeItems,
        stabilized: true,
        log: `> RS232: [STX] ${(currentGrossWeight - (bowlTareActive ? BOWL_WEIGHT : 0)).toFixed(1)}g [ETX] • MANUAL TRAY ADDED`,
        reactionTitle: `⚡ Item Detected: ${cleanName} (+${addGrams}g)`,
        reactionDesc: `POS Core added item. New net weight: ${(currentGrossWeight - (bowlTareActive ? BOWL_WEIGHT : 0)).toFixed(1)}g`,
        reactionStatus: 'active',
      };

      updateUi(customStage, true, false);
      playTickSound(780);

      btn.classList.add('pulse-active');
      setTimeout(() => btn.classList.remove('pulse-active'), 300);
    });
  });

  if (tareToggleBtn) {
    tareToggleBtn.addEventListener('click', () => {
      bowlTareActive = !bowlTareActive;
      if (tareStatusBadge) {
        tareStatusBadge.textContent = bowlTareActive ? 'TARE: AUTO (-120g)' : 'TARE: OFF (0g)';
        tareStatusBadge.style.background = bowlTareActive ? 'rgba(5, 150, 105, 0.12)' : 'rgba(249, 115, 22, 0.14)';
        tareStatusBadge.style.color = bowlTareActive ? '#059669' : '#ea580c';
      }
      playTickSound(920);

      const tareStage = {
        stage: currentStageIndex,
        title: bowlTareActive ? "Auto-Tare Calibrated (-120.0g)" : "Tare Disabled (Gross Weight Billed)",
        gross: currentGrossWeight,
        items: activeItems,
        stabilized: false,
        log: bowlTareActive ? "> RS232: ZERO TARE -120g CALIBRATED" : "> RS232: ZERO TARE DISABLED",
        reactionTitle: bowlTareActive ? "Zero Tare Calibrated (-120g)" : "⚠️ Raw Gross Weight Active",
        reactionDesc: bowlTareActive ? "Bowl weight automatically subtracted." : "Bowl tare removed. Only use for non-standard containers.",
        reactionStatus: 'listening',
      };
      updateUi(tareStage, false, false);
    });
  }

  brothButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      brothButtons.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      selectedBroth = btn.getAttribute('data-broth-name') || 'Signature Mala';
      if (customerSpiceDisplay) customerSpiceDisplay.textContent = selectedBroth;
      playTickSound(840);
    });
  });

  // Architectural Comparison vs Deconstruct vs Reconstruct view toggles
  const viewToggleBtns = document.querySelectorAll('[data-mala-view]');
  const deconstructCard = document.getElementById('mala-view-deconstruct');
  const reconstructCard = document.getElementById('mala-view-reconstruct');
  const deconstructGrid = document.querySelector('.deconstruct-grid');

  if (viewToggleBtns.length && deconstructCard && reconstructCard) {
    viewToggleBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const view = btn.getAttribute('data-mala-view');
        viewToggleBtns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');

        if (view === 'deconstruct') {
          deconstructCard.style.display = 'block';
          reconstructCard.style.display = 'none';
          if (deconstructGrid) {
            deconstructGrid.classList.add('single-view');
          }
        } else if (view === 'reconstruct') {
          deconstructCard.style.display = 'none';
          reconstructCard.style.display = 'block';
          if (deconstructGrid) {
            deconstructGrid.classList.add('single-view');
          }
        } else {
          // 'comparison'
          deconstructCard.style.display = 'block';
          reconstructCard.style.display = 'block';
          if (deconstructGrid) {
            deconstructGrid.classList.remove('single-view');
          }
        }
        playTickSound(720, 0.05);
      });
    });
  }

  // Listen to language switch events to instantly update text in active language
  window.addEventListener('msgtech_lang_changed', () => {
    updateUi(SCROLL_STAGES[currentStageIndex], false, false);
  });

  // Initial render at stage 0 (Tare Calibrated, Platter Ready)
  applyStage(0, false, false, false);
}
